(() => {
  'use strict';

  const selectors = {
    grid: '[data-infinite-grid], #AjaxinateLoop',
    pagination: '[data-infinite-pagination], #AjaxinatePagination',
    trigger: '[data-infinite-trigger]',
    next: '[data-infinite-next]',
    status: '[data-infinite-status]',
    item: '.grid__item, [data-product-block]',
    itemLink: '[data-grid-link]',
  };

  class CollectionInfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.onIntersect = this.onIntersect.bind(this);
    }

    connectedCallback() {
      this.reinit();
    }

    disconnectedCallback() {
      this.unload();
    }

    reinit() {
      this.unload();

      this.grid = this.querySelector(selectors.grid);
      this.pagination = this.querySelector(selectors.pagination);
      this.trigger = this.pagination?.querySelector(selectors.trigger);
      this.status = this.pagination?.querySelector(selectors.status);
      this.autoLoadLimit = Number(this.pagination?.dataset.autoLoadLimit || this.dataset.autoLoadLimit || 4);
      this.autoLoadCount = 0;
      this.isLoading = false;
      this.loadedUrls = new Set();
      this.loadedItemKeys = new Set();
      this.seedLoadedItems();

      this.nextUrl = this.getNextUrl(this.pagination);

      if (!this.grid || !this.pagination || !this.nextUrl || !('IntersectionObserver' in window)) {
        this.setStatus('');
        return;
      }

      this.classList.add('is-loaded');
      this.pagination.setAttribute('data-infinite-enhanced', 'true');
      this.observe();
    }

    unload() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    observe() {
      if (!this.trigger || !this.nextUrl || this.autoLoadCount >= this.autoLoadLimit) {
        return;
      }

      if (this.observer) {
        this.observer.disconnect();
      }

      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '700px 0px 900px',
        threshold: 0,
      });
      this.observer.observe(this.trigger);
    }

    onIntersect(entries) {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.loadNextPage();
      }
    }

    async loadNextPage() {
      if (this.isLoading || !this.nextUrl) {
        return;
      }

      if (this.autoLoadCount >= this.autoLoadLimit) {
        this.pauseAutoLoading('Automatic loading paused. Use the pagination links to continue.');
        return;
      }

      const url = this.normalizeUrl(this.nextUrl);
      if (!url || this.loadedUrls.has(url)) {
        this.pauseAutoLoading('');
        return;
      }

      this.isLoading = true;
      this.loadedUrls.add(url);
      this.setStatus('Loading more products.');
      this.pagination.classList.add('is-loading');
      this.observer?.disconnect();

      try {
        this.abortController = new AbortController();
        const response = await fetch(url, {
          credentials: 'same-origin',
          signal: this.abortController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          throw new Error(`Unable to load ${url}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = this.getMatchingElement(doc, selectors.grid, this.grid);
        const nextPagination = this.getMatchingElement(doc, selectors.pagination, this.pagination);

        if (!nextGrid) {
          throw new Error('No product grid found in paginated response.');
        }

        const appendedCount = this.appendItems(nextGrid);

        if (nextPagination) {
          this.pagination.innerHTML = nextPagination.innerHTML;
          this.trigger = this.pagination.querySelector(selectors.trigger);
          this.status = this.pagination.querySelector(selectors.status);
          this.nextUrl = this.getNextUrl(this.pagination);
        } else {
          this.nextUrl = '';
        }

        if (appendedCount > 0) {
          this.autoLoadCount += 1;
          this.updateBrowserUrl(url);
          this.afterAppend(appendedCount, url);
        }

        if (!this.nextUrl) {
          this.finishLoading('All products have loaded.');
        } else if (this.autoLoadCount >= this.autoLoadLimit) {
          this.pauseAutoLoading('Automatic loading paused. Use the pagination links to continue.');
        } else {
          this.observe();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn(error);
          this.loadedUrls.delete(url);
          this.pauseAutoLoading('Products could not auto-load. Use the pagination links to continue.');
        }
      } finally {
        this.isLoading = false;
        this.abortController = null;
        this.pagination?.classList.remove('is-loading');
      }
    }

    appendItems(sourceGrid) {
      const fragment = document.createDocumentFragment();
      let appendedCount = 0;

      Array.from(sourceGrid.children)
        .filter((item) => item.matches(selectors.item))
        .forEach((item) => {
          const key = this.getItemKey(item);

          if (key && this.loadedItemKeys.has(key)) {
            return;
          }

          if (key) {
            this.loadedItemKeys.add(key);
          }

          const clone = document.importNode(item, true);
          clone.classList.add('loaded-item');
          fragment.appendChild(clone);
          appendedCount += 1;
        });

      if (appendedCount > 0) {
        this.grid.appendChild(fragment);
      }

      return appendedCount;
    }

    seedLoadedItems() {
      this.grid?.querySelectorAll(selectors.item).forEach((item) => {
        const key = this.getItemKey(item);
        if (key) {
          this.loadedItemKeys.add(key);
        }
      });
    }

    getItemKey(item) {
      const link = item.querySelector(selectors.itemLink);
      if (!link) {
        return item.id || '';
      }

      const url = new URL(link.getAttribute('href'), window.location.origin);
      return url.pathname.replace(/\/$/, '');
    }

    getMatchingElement(doc, selector, currentElement) {
      const ajaxinateId = currentElement?.dataset.ajaxinateId;
      const candidates = Array.from(doc.querySelectorAll(selector));

      if (ajaxinateId) {
        return candidates.find((element) => element.dataset.ajaxinateId === ajaxinateId) || candidates[0] || null;
      }

      return candidates[0] || null;
    }

    getNextUrl(scope) {
      const explicitNext = scope?.querySelector(selectors.next);
      if (explicitNext?.href) {
        return explicitNext.href;
      }

      const nextLink = Array.from(scope?.querySelectorAll('a[href]') || []).find((link) => {
        const text = link.textContent.trim();
        return link.closest('.next') || /next/i.test(text) || link.getAttribute('rel') === 'next';
      });

      return nextLink?.href || '';
    }

    normalizeUrl(url) {
      if (!url) {
        return '';
      }

      const normalized = new URL(url, window.location.href);
      normalized.hash = '';
      normalized.searchParams.delete('section_id');
      return normalized.toString();
    }

    updateBrowserUrl(url) {
      if (!window.history?.replaceState) {
        return;
      }

      const next = new URL(url, window.location.href);
      window.history.replaceState({infiniteScrollPage: next.searchParams.get('page')}, '', `${next.pathname}${next.search}`);
    }

    afterAppend(count, url) {
      this.setStatus(`${count} more products loaded.`);

      window.theme?.setVars?.();
      window.AOS?.refreshHard?.();
      document.dispatchEvent(new CustomEvent('theme:scroll'));
      document.dispatchEvent(
        new CustomEvent('collection:infinite-scroll:loaded', {
          detail: {count, url},
        })
      );
    }

    finishLoading(message) {
      this.setStatus(message);
      this.removeTrigger();
      this.unload();
    }

    pauseAutoLoading(message) {
      this.setStatus(message);
      this.removeTrigger();
      this.unload();
    }

    removeTrigger() {
      this.pagination?.querySelector(selectors.trigger)?.remove();
      this.trigger = null;
    }

    setStatus(message) {
      if (this.status) {
        this.status.textContent = message;
      }
    }
  }

  if (!customElements.get('ajaxinate-items')) {
    customElements.define('ajaxinate-items', CollectionInfiniteScroll);
  }
})();
